import FuseSplashScreen from '@fuse/core/FuseSplashScreen';
import History from '@history';
import { STORAGE_KEYS } from 'app/constants/storage.constant';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import { adminApi } from 'app/main/apps/admin/api';
import getCategories from 'app/main/apps/categories/ducks/categories.thunk';
import { getReachableFranchisesApi } from 'app/main/apps/franchises/api';
import { getUserFranchise } from 'app/main/apps/franchises/ducks/franchises.thunk';
import { getLoyaltyModel } from 'app/main/apps/loyalties/ducks/loyalty.thunk';
import { getStores, setCurrentStoreIdThunk } from 'app/main/apps/stores/ducks/stores.thunk';
import { getWebPushSubscriptionThunk } from 'app/main/apps/webPush/ducks/webPush.thunk';
import { getIpInfo, switchUserLanguage } from 'app/services/ipInfoService';
import jwtService from 'app/services/jwtService';
import { hideMessage, showMessage } from 'app/store/fuse/messageSlice';
import { replaceNavigation } from 'app/store/fuse/navigationSlice';
import React, { useEffect, useState } from 'react';
import { ConnectedProps, connect, useDispatch } from 'react-redux';
import authRoles, { isRoleEnum, RoleEnum, SpecialRolesEnum } from './authRoles';
import { setSuperAdmin } from './store/admin.slice';
import { UserModelInDTO } from './store/type';
import { getOwnerPermissionsThunk, logoutUser } from './store/user.thunk';
import { setUserData, setUserLocation } from './store/userSlice';
import { getNavigationConfig, getUserRole, shouldPickBrand } from './utils/utils';

type AuthProps = ConnectedAuthProps;

const Auth: React.FC<AuthProps> = props => {
  const [waitAuthCheck, setWaitAuthCheck] = useState(true);
  const dispatch = useDispatch();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation_todo>
  useEffect(() => {
    const loadLanguage = async () => {
      getIpInfo().then(ipInfo => {
        if (ipInfo) {
          switchUserLanguage(ipInfo);
          props.setUserLocation(ipInfo);
        }
      });
    };
    loadLanguage();
    jwtCheck().then(() => setWaitAuthCheck(false));
  }, []);

  const jwtCheck = () =>
    new Promise<void>(resolve => {
      jwtService.on('onAutoLogin', async () => {
        try {
          /**
           * A delegated brand session is minted fresh on every boot from the caller's
           * own token, because the delegated one is short-lived on purpose.
           *
           * Except under an admin impersonation, which rebuilds the whole session below
           * from `user_id_lb` - and the brand session has pointed that at the brand's
           * owner. Restoring it first would make the admin impersonate that owner
           * instead of the operator they chose. Drop the brand instead and land on the
           * operator's own one; the admin can switch from there.
           */
          const activeFranchiseId = jwtService.getActiveFranchiseId();
          if (activeFranchiseId) {
            if (jwtService.getImpostorToken()) {
              jwtService.leaveFranchise();
            } else {
              await jwtService.restoreFranchiseSession(activeFranchiseId);
            }
          }

          /**
           * Sign in and retrieve user data from Api
           */
          if (jwtService.getImpostorToken()) {
            localStorage.setItem(STORAGE_KEYS.JWT_ACCESS_TOKEN, jwtService.getImpostorToken() as string);
            localStorage.removeItem(STORAGE_KEYS.JWT_IMPOSTOR_TOKEN);
          }
          const user: UserModelInDTO = await jwtService.signInWithUserId();
          const impostor = await adminApi.api.get<UserModelInDTO>(`/api/v2/users/me`).then(res => res.data);
          if (impostor.acl && authRoles.admin.includes(impostor.acl.role)) {
            props.setSuperAdmin({ isSuperAdmin: true, email: impostor.email, id: impostor.id });
          } else if (impostor.acl && !authRoles.owner.includes(impostor.acl.role)) {
            jwtService.logout();
            resolve();
          }
          if (user.id !== impostor.id) {
            await jwtService.createImpostorToken(user.id, impostor.id);
          }

          const userRole = getUserRole(user);

          const merchantRoles = [RoleEnum.STORE_OWNER, RoleEnum.FRANCHISE_OWNER];
          const hasUserRoleMerchant = merchantRoles.some(role => userRole === role);
          if (hasUserRoleMerchant) {
            const franchiseId = await props.getUserFranchise();
            if (!franchiseId) {
              jwtService.logout();
              resolve();
              return;
            }
            const stores = await props.getStoresThunk();
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              await props.getWebPushSubscriptionThunk();
            }
            // More than one brand is what puts the switcher in the user menu; a
            // failure here must not cost the merchant their session.
            const reachableFranchises = await getReachableFranchisesApi().catch(() => []);
            props.setUserData({
              userData: user,
              role: stores.length === 0 ? SpecialRolesEnum.RESTRICTED_STORE_OWNER : userRole,
              reachableFranchisesCount: reachableFranchises.length,
            });
            props.setCurrentStoreIdThunk();
            dispatch(getLoyaltyModel());
            dispatch(getOwnerPermissionsThunk(franchiseId, user.id));

            // One brand behaves exactly as before. Several, and the operator has to say
            // which one before landing anywhere - once per session, not on every reload.
            if (shouldPickBrand(reachableFranchises.length, jwtService.hasPickedBrand())) {
              History.push(routesUrl.myBrands);
            }
          } else if (userRole && isRoleEnum(userRole) && !authRoles.admin.includes(userRole)) {
            jwtService.logout();
            resolve();
          } else {
            props.setUserData({ userData: user, role: userRole });
          }
          const navConfig = getNavigationConfig();
          props.replaceNavigation(navConfig);

          props.getCategories();
          resolve();
        } catch (error: any) {
          props.showMessage({ message: error.message });
          resolve();
        }
      });

      jwtService.on('onAutoLogout', (message: string) => {
        if (message) {
          props.showMessage({ message });
        }

        props.logout();

        resolve();
      });

      jwtService.on('onNoAccessToken', () => {
        resolve();
      });

      jwtService.init();

      return Promise.resolve();
    });

  return waitAuthCheck ? <FuseSplashScreen /> : <>{props.children}</>;
};

const mapDispatchToProps = {
  logout: logoutUser,
  getStoresThunk: getStores,
  getWebPushSubscriptionThunk,
  setCurrentStoreIdThunk,
  getUserFranchise,
  getCategories,
  setUserData,
  replaceNavigation,
  setUserLocation,
  showMessage,
  hideMessage,
  setSuperAdmin,
};

const connector = connect(undefined, mapDispatchToProps);

type ConnectedAuthProps = ConnectedProps<typeof connector>;

export default connector(Auth);
